import { api } from "./client";
import { apiRoutes } from "util/apiRoutes";
import { Comment } from "util/constants";
import type { PrivacyLevel } from "util/userUtils";

export interface CreateCommentPayload {
  content: string;
  parent_id?: number;
  kind?: string;
  status?: string;
  properties?: Record<string, unknown> & {
    privacy?: PrivacyLevel;
  };
  poll?: Record<string, unknown>;
  place_id?: number | null;
  timeline_ts?: string | null;
}

export interface GetByCommentableParams {
  /**
   * Legacy single-kind filter (e.g. `"principle"`). Use `kinds` when
   * you need to pin more than one kind.
   */
  kind?: string;
  /**
   * Explicit allowlist of comment kinds. Overrides the backend's
   * default behavior (which hides `task`/`principle`/`edge_note` but
   * already shows `argument` on Discussion surfaces).
   *
   * Serializes to `?kinds=kind1,kind2`. When the surface is an issue
   * Discussion AND this list contains `'argument'`, the backend
   * inlines `kind='argument'` rows attached to this issue's child
   * teabates via a `posts.issue_id` join — there is no separate
   * `include_arguments` flag anymore; the kinds list is the only
   * knob.
   */
  kinds?: ReadonlyArray<string>;
}

export const commentApi = {
  getByCommentable: async (
    commentableType: Comment["commentable_type"],
    commentableId: number,
    params?: GetByCommentableParams
  ): Promise<Comment[]> => {
    const { kinds, ...rest } = params ?? {};
    const queryParams: Record<string, string | undefined> = { ...rest };
    if (Array.isArray(kinds) && kinds.length > 0) {
      const normalized = Array.from(
        new Set(
          kinds
            .map((value) => (typeof value === "string" ? value.trim().toLowerCase() : ""))
            .filter((value) => value.length > 0)
        )
      );
      if (normalized.length > 0) {
        queryParams.kinds = normalized.join(",");
      }
    }
    const response = await api.get<Comment[]>(apiRoutes.comments.byCommentable(commentableType, commentableId), {
      params: queryParams,
    });
    return response.data;
  },
  createForCommentable: async (
    commentableType: Comment["commentable_type"],
    commentableId: number,
    payload: CreateCommentPayload
  ): Promise<Comment> => {
    const response = await api.post<Comment>(apiRoutes.comments.byCommentable(commentableType, commentableId), payload);
    return response.data;
  },
};

